'use client';

import { PropsWithChildren } from 'react';
import ReactQueryProvider from '@/providers/ReactQueryProvider';
import AmplitudeProvider from '@/providers/AmplitudeProvider';
import { AuthProvider } from '@/lib/auth/auth-context';
import { CurrencyProvider } from '@/lib/currency/context';
import RootLayoutClient from './RootLayoutClient';
import { usePathname } from 'next/navigation';
import { Toaster } from 'sonner';
import { PWAInstallPrompt } from '@/components/PWAInstallPrompt';
import { ServiceWorkerRegistration } from '@/components/ServiceWorkerRegistration';

export function ClientProviders({ children }: PropsWithChildren) {
  const pathname = usePathname();
  
  // List of public routes that don't need the NavMenu and related UI
  const isPublicRoute = [
    '/',
    '/login',
    '/signup',
    '/subscription-payment',
    '/debug',
    '/privacy-policy',
    '/terms-and-conditions',
    '/cookie-policy',
    '/cancellation-and-refund',
    '/shipping-and-delivery'
  ].some(route => pathname === route || pathname?.startsWith(`${route}?`)) || 
  // Check if it's a dynamic [code] route (public order page) - but exclude known private routes
  (/^\/[^/]+$/.test(pathname || '') && 
   ![
     '/orders',
     '/calendar',
     '/products',
     '/analytics',
     '/settings',
     '/profile',
     '/menu-website',
     '/payments',
     '/customers',
     '/dashboard',
   ].includes(pathname || ''));
  
  return (
    <AmplitudeProvider>
      <ReactQueryProvider>
        <AuthProvider>
          <CurrencyProvider>
            <ServiceWorkerRegistration />
            {isPublicRoute ? (
              // Simple layout for public pages
              <div className="min-h-screen">
                {children}
                <PWAInstallPrompt />
                <Toaster richColors position="top-right" />
              </div>
            ) : (
              // Full authenticated layout for private pages
              <RootLayoutClient>
                {children}
                <PWAInstallPrompt />
              </RootLayoutClient>
            )}
          </CurrencyProvider>
        </AuthProvider>
      </ReactQueryProvider>
    </AmplitudeProvider>
  );
}
